/**
 * V2 Stamping run — /category/{slug}/stamp (default-app wave 2, step 2).
 *
 * The bulk answer to "log them to make them count": a signed-in explorer
 * picks the collection members they've already visited (the signed-out
 * tally pre-checks itself), then walks them one at a time — the app finds
 * each place on the map, they pick a photo and an optional date, tap
 * Stamp. Every stamp is a REAL yoink (V1 write shape); progress math
 * never widens (DEFAULT-APP-2026-08 standing decision 1) and the photo
 * requirement holds (decision 2) — a park without a photo stays on the
 * "still to stamp" list instead of becoming a photo-less card.
 *
 * Place resolution per leaf, cheapest first:
 *   1. the location most yoinked in that category already (no Google
 *      call, no duplicate row);
 *   2. findPlaceByName on the category's name (Text Search + the
 *      conservative matchesName gate) — held as a PENDING place and only
 *      written at stamp time (the 2026-07-27 deferred-creation rule).
 * Resolution happens while the person handles the previous card, so the
 * walk is human-paced — well under the proxy's rate limit.
 */
import * as gateway from '../../core/gateway.js';
import { invalidate } from '../../core/query.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { fetchAllCategories, categorySlug, findCategoryBySlug } from '../../core/categories.js';
import { categoryIcon } from '../../ui/icons.js';
import { findPlaceByName, pendingFromNearby, savePendingLocation } from '../log/places.js';
import { resolvePhotos } from '../log/log-data.js';
import { validatePhotoPick, toDateInput } from '../log/log-validate.js';
import { fetchMyYoinkedCategoryIds } from './category-data.js';
import { buildSetTree } from './category-stats.js';
import { markComplete } from '../community/tutorial.js';
import { syncAwards } from '../profile/awards.js';
import { crossOffWantToGo } from '../lists/lists.js';
import { READ_ONLY_MESSAGE } from '../../core/settings.js';

/** Tree leaves in display order — the stampable choices. Exported for tests. */
export function treeLeaves(tree) {
  const out = [];
  const walk = (nodes) => nodes.forEach((n) => (n.kids.length ? walk(n.kids) : out.push(n)));
  walk(tree);
  return out;
}

/**
 * The location this category's yoinks already live at, when there is one
 * — most-yoinked first so a stray mis-filed row can't win. Returns the
 * log-flow location shape or null. Exported for tests.
 */
export function mostYoinkedLocation(rows) {
  const counts = new Map();
  for (const r of rows || []) {
    if (!r.locations?.id) continue;
    const entry = counts.get(r.locations.id) || { location: r.locations, n: 0 };
    entry.n += 1;
    counts.set(r.locations.id, entry);
  }
  const best = [...counts.values()].sort((a, b) => b.n - a.n)[0];
  return best ? best.location : null;
}

export function mount(el, ctx) {
  const { store, router, params } = ctx;
  let destroyed = false;
  let category = null; // the set
  let catalog = [];
  let leaves = []; // [{ cat, done }]
  let doneCount = 0;
  let checked = new Set(); // category ids picked on the checklist
  let queue = []; // leaf cats still to walk
  let current = null; // { cat, resolved: null|{location}|{pending}|'none' }
  let photoItems = []; // [{ file }] for the current card
  let photoUrls = []; // blob URLs to revoke
  let visitDate = '';
  let stamping = false;
  let cardError = '';
  let stamped = []; // category names stamped this run
  let skipped = []; // leaf cats skipped this run

  function tallyKey() {
    return `yoink_tally_${category.id}`;
  }

  function loadTally() {
    try {
      const stored = JSON.parse(localStorage.getItem(tallyKey()) || '[]');
      return Array.isArray(stored) ? new Set(stored) : new Set();
    } catch {
      return new Set();
    }
  }

  /** Stamped ids leave the tally (they count for real now); skips stay. */
  function pruneTally(ids) {
    try {
      const kept = [...loadTally()].filter((id) => !ids.has(id));
      localStorage.setItem(tallyKey(), JSON.stringify(kept));
    } catch { /* private mode — nothing to prune */ }
  }

  function clearPhotoUrls() {
    photoUrls.forEach((u) => URL.revokeObjectURL(u));
    photoUrls = [];
  }

  const back = () => `<nav class="v2-back" aria-label="Back">
    <a href="#" data-stamp-back>← ${escapeHtml(category?.name || 'Back')}</a></nav>`;

  function renderGate(message) {
    el.innerHTML = `${category ? back() : ''}<div class="v2-empty">${message}</div>`;
  }

  function renderChecklist() {
    const stampable = leaves.filter((l) => !l.done);
    const rowHtml = (l) => l.done
      ? `<div class="stamp-row stamp-row-done">
           ${categoryIcon(l.cat, 18)}
           <span class="set-child-name">${escapeHtml(l.cat.name)}</span>
           <span class="stamp-row-note">✓ already stamped</span>
         </div>`
      : `<label class="stamp-row">
           <input type="checkbox" data-stamp-check="${l.cat.id}" ${checked.has(l.cat.id) ? 'checked' : ''}>
           ${categoryIcon(l.cat, 18)}
           <span class="set-child-name">${escapeHtml(l.cat.name)}</span>
         </label>`;
    el.innerHTML = `
      ${back()}
      <section class="v2-import" aria-label="Mark your visits">
        <h2 class="v2-page-title">Mark your visits</h2>
        <p>Tick the ones you’ve been to. Then we’ll walk them one at a time —
          pick a photo from your trip, add a rough date if you remember it,
          and each becomes a real stamp in your journal.</p>
        <div class="stamp-rows">${leaves.map(rowHtml).join('')}</div>
        <button type="button" class="v2-btn-leather" data-stamp-start ${checked.size ? '' : 'disabled'}>
          Stamp ${checked.size || ''} ${checked.size === 1 ? 'visit' : 'visits'}</button>
        ${stampable.length ? '' : '<p class="log-hint">Everything here is already stamped — nothing to do!</p>'}
      </section>`;
  }

  function photoListHtml() {
    if (!photoItems.length) return '';
    return `<div class="stamp-photos">${photoItems
      .map((item, i) => `<span class="stamp-photo"><img src="${photoUrls[i]}" alt=""></span>`)
      .join('')}</div>`;
  }

  function renderCard() {
    const total = stamped.length + skipped.length + queue.length + 1;
    const at = stamped.length + skipped.length + 1;
    const r = current.resolved;
    const placeLine = r === null
      ? '<p class="log-hint" data-stamp-place aria-live="polite">Finding it on the map…</p>'
      : r === 'none'
        ? '<p class="log-hint" data-stamp-place>Couldn’t find it on the map — skip it for now and add it later from the Log page.</p>'
        : `<p class="stamp-place" data-stamp-place>📍 ${escapeHtml(r.display_name)}${r.formatted_address ? ` <span class="stamp-place-sub">${escapeHtml(r.formatted_address)}</span>` : ''}</p>`;
    el.innerHTML = `
      ${back()}
      <section class="v2-import" aria-label="Stamp ${escapeHtml(current.cat.name)}">
        <p class="stamp-progress">${at} of ${total}</p>
        <h2 class="v2-page-title">${categoryIcon(current.cat, 24)} ${escapeHtml(current.cat.name)}</h2>
        ${placeLine}
        <label class="v2-btn-leather import-file-btn">${photoItems.length ? 'Change photo' : 'Pick a photo from your trip'}
          <input type="file" accept="image/*" multiple data-stamp-photo hidden>
        </label>
        ${photoListHtml()}
        <label class="v2-field">When were you there? (a rough date is fine — blank means today)
          <input type="date" name="stampDate" value="${visitDate}" max="${toDateInput(new Date())}">
        </label>
        <div class="v2-form-error" data-stamp-error role="alert">${escapeHtml(cardError)}</div>
        <div class="log-status" data-stamp-status aria-live="polite"></div>
        <div class="stamp-actions">
          <button type="button" class="v2-btn-leather" data-stamp-save
            ${stamping || !photoItems.length || !r || r === 'none' ? 'disabled' : ''}>
            ${stamping ? 'Stamping…' : 'Stamp it'}</button>
          <button type="button" class="v2-btn-more" data-stamp-skip ${stamping ? 'disabled' : ''}>Skip for now</button>
        </div>
      </section>`;
  }

  function renderDone() {
    const newDone = doneCount + stamped.length;
    const total = leaves.length;
    const pct = total ? Math.round((newDone / total) * 100) : 0;
    el.innerHTML = `
      ${back()}
      <section class="v2-import" aria-label="Stamping finished">
        <h2 class="v2-page-title">${stamped.length ? 'Stamped!' : 'All done'}</h2>
        ${stamped.length ? `
        <div class="set-progress" aria-label="Your progress">
          <div class="set-progress-head">
            <h3 class="friends-title">Your progress</h3>
            <span class="set-progress-label">${newDone}/${total} (${pct}%)</span>
          </div>
          <div class="collection-bar set-progress-bar"><span class="collection-fill" style="width:${pct}%"></span></div>
          ${pct === 100 ? '<p class="set-complete">Complete!</p>' : ''}
        </div>
        <p>${stamped.length} ${stamped.length === 1 ? 'visit is' : 'visits are'} in your journal now.</p>` : ''}
        ${skipped.length ? `<p class="log-hint">Still to stamp: ${skipped.map((c) => escapeHtml(c.name)).join(', ')} —
          come back anytime, your ticks are remembered.</p>` : ''}
        <button type="button" class="v2-btn-leather" data-stamp-back>See the collection</button>
      </section>`;
  }

  /** Cheapest-first place resolution for a leaf (see header). */
  async function resolveLeaf(cat) {
    try {
      const { data } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
        select: 'id,locations(id,display_name,formatted_address)',
        filters: { category_id: `eq.${cat.id}` },
        limit: 25,
      });
      const existing = mostYoinkedLocation(data);
      if (existing) return { ...existing, existing: true };
    } catch { /* fall through to the name search */ }
    try {
      const place = await findPlaceByName(cat.name, null, null);
      if (place) return { ...pendingFromNearby(place), display_name: place.display_name, formatted_address: place.formatted_address };
    } catch { /* offline / proxy down — 'none' below says so */ }
    return 'none';
  }

  function advance() {
    clearPhotoUrls();
    photoItems = [];
    visitDate = '';
    cardError = '';
    stamping = false;
    if (!queue.length) {
      current = null;
      finishRun();
      renderDone();
      return;
    }
    const cat = queue.shift();
    current = { cat, resolved: null };
    renderCard();
    resolveLeaf(cat).then((resolved) => {
      if (destroyed || current?.cat !== cat) return;
      current.resolved = resolved;
      renderCard();
    });
  }

  function finishRun() {
    if (!stamped.length) return;
    pruneTally(new Set(stamped.map((c) => c.id)));
    invalidate(); // feeds, the set checklist, profile — all just changed
    markComplete('log_yoink');
    const user = store.get().user;
    if (user) syncAwards(store, user.id, { announce: true }).catch(() => {});
  }

  async function stampCurrent() {
    const user = store.get().user;
    const r = current?.resolved;
    if (!user || !r || r === 'none' || stamping) return;
    if (!photoItems.length) {
      cardError = 'Pick a photo first — every stamp carries one.';
      renderCard();
      return;
    }
    if (visitDate && visitDate > toDateInput(new Date())) {
      cardError = 'The date can’t be in the future.';
      renderCard();
      return;
    }
    stamping = true;
    cardError = '';
    renderCard();
    const setStatus = (text) => {
      const line = el.querySelector('[data-stamp-status]');
      if (line) line.textContent = text;
    };
    try {
      const location = r.pending ? await savePendingLocation(r) : r;
      if (destroyed) return;
      const images = await resolvePhotos(user.id, photoItems, setStatus);
      if (destroyed) return;
      setStatus('Saving…');
      const body = {
        user_id: user.id,
        location_id: location.id,
        category_id: current.cat.id,
        title: location.display_name || current.cat.name,
        description: null,
        social_url: null,
        images,
      };
      // Same backdate rule as the log form: the picked date keeps the
      // current time-of-day, so a run of stamps stays in tap order.
      if (visitDate) {
        const [y, m, d] = visitDate.split('-').map(Number);
        const when = new Date();
        when.setFullYear(y, m - 1, d);
        body.created_at = when.toISOString();
      }
      const { error } = await gateway.rawInsert(gateway.TABLES.YOINKS, { body });
      if (error) throw error;
      crossOffWantToGo(store, location.id).catch(() => {});
      stamped.push(current.cat);
      advance();
    } catch (err) {
      stamping = false;
      cardError = err?.message ? `Couldn’t stamp it — ${err.message}` : 'Couldn’t stamp it — try again.';
      renderCard();
    }
  }

  function onChange(event) {
    const check = event.target.closest('[data-stamp-check]');
    if (check) {
      const id = check.dataset.stampCheck;
      if (check.checked) checked.add(id);
      else checked.delete(id);
      const start = el.querySelector('[data-stamp-start]');
      if (start) {
        start.disabled = !checked.size;
        start.textContent = `Stamp ${checked.size || ''} ${checked.size === 1 ? 'visit' : 'visits'}`;
      }
      return;
    }
    const photo = event.target.closest('[data-stamp-photo]');
    if (photo && photo.files?.length) {
      const { accepted, error } = validatePhotoPick([], [...photo.files]);
      cardError = error;
      if (accepted.length) {
        clearPhotoUrls();
        photoItems = accepted;
        photoUrls = photoItems.map((item) => URL.createObjectURL(item.file));
      }
      renderCard();
      return;
    }
    if (event.target.name === 'stampDate') visitDate = event.target.value;
  }

  function onClick(event) {
    if (event.target.closest('[data-stamp-start]')) {
      queue = leaves.filter((l) => !l.done && checked.has(l.cat.id)).map((l) => l.cat);
      if (queue.length) advance();
      return;
    }
    if (event.target.closest('[data-stamp-save]')) {
      stampCurrent();
      return;
    }
    if (event.target.closest('[data-stamp-skip]')) {
      if (current) skipped.push(current.cat);
      advance();
      return;
    }
    const backLink = event.target.closest('[data-stamp-back]');
    if (backLink) {
      event.preventDefault();
      if (category) router.navigate('category', { slug: categorySlug(category) });
      else router.navigate('discover');
    }
  }

  async function load() {
    const [cat, cats] = await Promise.all([findCategoryBySlug(params.slug), fetchAllCategories()]);
    if (destroyed) return;
    category = cat;
    catalog = cats;
    if (!category) {
      renderGate('That collection doesn’t exist.');
      return;
    }
    if (!category.is_set) {
      router.navigate('category', { slug: params.slug }, { replace: true });
      return;
    }
    const user = store.get().user;
    const mine = await fetchMyYoinkedCategoryIds(user.id).catch(() => new Set());
    if (destroyed) return;
    const tree = buildSetTree(catalog, category.id, mine);
    leaves = treeLeaves(tree).map((n) => ({ cat: n.cat, done: n.done }));
    doneCount = leaves.filter((l) => l.done).length;
    const tally = loadTally();
    checked = new Set(leaves.filter((l) => !l.done && tally.has(l.cat.id)).map((l) => l.cat.id));
    renderChecklist();
  }

  function start() {
    const state = store.get();
    if (!state.authReady) {
      const unsub = store.subscribe((s) => {
        if (!s.authReady || destroyed) return;
        unsub();
        start();
      });
      el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
      return;
    }
    if (!state.user) {
      el.innerHTML = '<div class="v2-empty">Sign in (top right) to stamp your visits.</div>';
      return;
    }
    if (state.isReadOnly) {
      el.innerHTML = `<div class="v2-empty">${READ_ONLY_MESSAGE}</div>`;
      return;
    }
    el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
    load().catch(() => {
      if (!destroyed) renderGate('Couldn’t load this collection — try again.');
    });
  }

  el.addEventListener('change', onChange);
  el.addEventListener('click', onClick);
  start();

  return () => {
    destroyed = true;
    clearPhotoUrls();
    el.removeEventListener('change', onChange);
    el.removeEventListener('click', onClick);
  };
}
